import { FocusEvent, MouseEvent, type ReactNode } from 'react'
import classNames from 'classnames/bind'

import { emptyObject } from 'app/common/lib/empty'

import { SurfaceType } from '../plain-dropdown-surface'
import styles from './plain-button-as-link.module.scss';

const cx = classNames.bind(styles)

export type Props = {
  children: ReactNode
  type?: 'button' | 'submit' // 'reset' type forbidden, do not use
  id?: string
  ariaLabel?: string
  disabled?: boolean
  className?: string
  linkClassName?: string
  tabIndex?: string
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void
  ariaHaspopup?: SurfaceType
  'aria-haspopup'?: SurfaceType
  ariaExpanded?: 'true' | 'false'
  'aria-expanded'?: 'true' | 'false'
}

/**
 * Компонент кнопки в стиле ссылки.
 * Используется для ссылок, не ведущих на другую страницу.
 *
 * @typedef {Object} Props
 * @property {React.ReactNode} children - Содержимое компонента.
 * @property {'button'|'submit'} type - Тип кнопки (по умолчанию "button").
 * @property {string} id - Идентификатор кнопки.
 * @property {string} ariaLabel - Текст для чтения с помощью экранного ридера.
 * @property {boolean} disabled - Определяет, является ли кнопка отключенной.
 * @property {string} className - Дополнительные классы для кнопки.
 * @property {string} linkClassName - Дополнительные классы для ссылки.
 * @property {string} tabIndex - Значение атрибута `tabIndex` для кнопки.
 * @property {function} onClick - Обработчик клика на кнопку.
 * @property {function} onFocus - Обработчик события `focus` для кнопки.
 * @property {function} onBlur - Обработчик события `blur` для кнопки.
 * @property {string} ariaHaspopup - Указывает, имеет ли кнопка связанное всплывающее меню.
 * @property {'true'|'false'} ariaExpanded - Указывает, является ли связанное всплывающее меню открытым.
 */
const PlainButtonAsLink = ({
  children,
  type,
  id,
  ariaLabel,
  disabled,
  tabIndex,
  onClick,
  className,
  linkClassName,
  ariaHaspopup,
  ariaExpanded,
  onFocus,
  onBlur,
}: Props) => {
  const props = {
    id,
    'aria-label': ariaLabel,
    onClick,
    disabled,
    className: cx(styles.root, className),
    'aria-haspopup': ariaHaspopup,
    'aria-expanded': ariaExpanded,
    onFocus,
    onBlur,
    ...(tabIndex ? { tabIndex: parseInt(tabIndex, 10) } : emptyObject),
  }

  const childrenContainer = (
    <span className={cx({ link: !disabled, disabled }, linkClassName)}>{children}</span>
  )

  // type must be static, so:
  if (type === 'submit') {
    return (
      <button type="submit" {...props}>
        {childrenContainer}
      </button>
    )
  }

  // default type: "button"
  return (
    <button type="button" {...props}>
      {childrenContainer}
    </button>
  )
}

export default PlainButtonAsLink
